import {rootEl} from '@ui/root-el';
import {observeSize} from '@ui/utils/dom/observe-size';
import {useEffect, useState} from 'react';

interface ViewportSize {
  width: number;
  height: number;
}

export function useOverlayViewport(): Record<string, string> {
  // if root el is not body, we are inside iframe or another
  // element and should use that element as a viewport instead
  const [rootElIsBody] = useState(() => rootEl === document.body);
  const [size, setSize] = useState(() => getViewportSize(rootElIsBody));

  useEffect(() => {
    const onResize = () => {
      setSize(size => {
        const newSize = getViewportSize(rootElIsBody);
        if (newSize.width === size.width && newSize.height === size.height) {
          return size;
        }
        return newSize;
      });
    };

    if (!rootElIsBody) {
      // The size is also capped by the screen now, so follow the window too.
      const stopObserving = observeSize({current: rootEl}, onResize);
      window.addEventListener('resize', onResize);
      return () => {
        stopObserving?.();
        window.removeEventListener('resize', onResize);
      };
    } else {
      const obj = visualViewport || window;
      obj.addEventListener('resize', onResize);
      return () => obj.removeEventListener('resize', onResize);
    }
  }, [rootElIsBody]);

  return {
    '--be-viewport-height': `${size.height}px`,
    '--be-viewport-width': `${size.width}px`,
  };
}

function getViewportSize(rootElIsBody: boolean): ViewportSize {
  const visualViewport = window.visualViewport;
  const screenWidth = visualViewport?.width || window.innerWidth;
  const screenHeight = visualViewport?.height || window.innerHeight;
  const width = (rootElIsBody && visualViewport?.width) || rootEl.clientWidth;
  const height = (rootElIsBody && visualViewport?.height) || rootEl.clientHeight;
  // Never larger than the screen. `#root` is only the viewport when the app
  // scrolls inside it; on public pages that scroll the document (a long
  // listing page), #root is as tall as the whole page — so a dialog's
  // "90% of the viewport" became thousands of pixels, the dialog ran off the
  // screen, and with the page scroll locked nothing could be scrolled at all.
  return {
    width: Math.min(width, screenWidth),
    height: Math.min(height, screenHeight),
  };
}
